import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Carousel,
  Spinner,
} from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useHistory } from 'react-router-dom';
import LiveInput from '../../../atoms/LiveInput/LiveInput';
import Icon from '../../../atoms/Icon/Icon';
import shortDateFormat from '../../../../helpers/dates';
import Sidebar from '../../../molecules/Sidebar/Sidebar';
import Api from '../../../../services/Api';
import { fetchBuildingsInitAction, setSelectedBuildingAction } from '../../../../actions/instances/buildings';
import DeleteModal from '../../../molecules/DeleteModal/DeleteModal';
import DocumentsList from '../../../Organisms/DocumentsList/DocumentsList';
import { loadBuildingsMetadata } from '../../../../helpers/metadata';
import CitationList from '../../../Organisms/CitationList/CitationList';
import ElementsList from '../../../Organisms/ElementsList/ElementsList';
import ComponentsList from '../../../Organisms/ComponentsList/ComponentsList';
import ContextsList from '../../../Organisms/ContextsList/ContextsList';
import ProductsCardsList from '../../../Organisms/ProductsCardsList/ProductsCardsList';
import CharacteristicsCardList from '../../../Organisms/CharacteristicsCardList/CharacteristicsCardList';
import { publishBuilding } from '../../../../helpers/publish';
import './ViewBuilding.scss';

const ViewBuilding = () => {
  const { selectedBuilding } = useSelector(state => state.buildings);
  const { user } = useSelector(state => state.auth);
  const [components, setComponents] = useState([]);
  const [elements, setElements] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [products, setProducts] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [citation, setCitation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const dispatch = useDispatch();
  const loadMetadata = async () => {
    const result = await loadBuildingsMetadata(selectedBuilding.id);
    setComponents(result.components);
    setElements(result.elements);
    setContexts(result.contexts);
    setProducts(result.products);
    setCharacteristics(result.characteristics);
    setCitation(result.citation);
  };
  useEffect(() => {
    setIsLoading(true);
    loadMetadata().then(() => setIsLoading(false));
  }, []);
  const history = useHistory();
  if (!selectedBuilding) {
    history.push('/buildings');
  }
  const canDeleteBuilding = () => (
    components.length === 0
    && elements.length === 0
    && products.length === 0
    && contexts.length === 0
    && characteristics.length === 0
  );
  const deleteBuilding = () => {
    const confirmed = window.confirm('Are you sure you want to delete this building?');
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    Api.delete(`/instances/buildings/${selectedBuilding.id}`).then((response) => {
      if (response.status === 204) {
        dispatch(fetchBuildingsInitAction());
        setTimeout(() => {
          history.goBack();
          setIsDeleting(false);
        }, 500);
      } else {
        setIsDeleting(false);
      }
    });
  };
  const syncComponents = async (newComponent) => {
    setIsLoading(true);
    const componentDetails = await Api.get(`instances/building-components/${newComponent.id}`);
    const newIds = [
      ...componentDetails.data.component_buildings.map(x => x.id),
      selectedBuilding.id,
    ];
    await Api.patch(`instances/building-components/${newComponent.id}`, {
      buildings_ids: newIds,
    });
    await loadMetadata();
    setIsLoading(false);
  };
  const syncElements = async (newElement) => {
    setIsLoading(true);
    const elementDetails = await Api.get(`instances/building-elements/${newElement.id}`);
    const newIds = [
      ...elementDetails.data.buildings.map(x => x.id),
      selectedBuilding.id,
    ];
    await Api.patch(`instances/building-elements/${newElement.id}`, {
      buildings_ids: newIds,
    });
    await loadMetadata();
    setIsLoading(false);
  };
  const syncContexts = async (newContext) => {
    setIsLoading(true);
    const contextDetails = await Api.get(`instances/building-contexts/${newContext.id}`);
    const newIds = [
      ...contextDetails.data.buildings.map(x => x.id),
      selectedBuilding.id,
    ];
    await Api.patch(`instances/building-contexts/${newContext.id}`, {
      buildings_ids: newIds,
    });
    await loadMetadata();
    setIsLoading(false);
  };
  const syncProducts = async (newProduct) => {
    setIsLoading(true);
    const productDetails = await Api.get(`instances/building-products/${newProduct.id}`);
    const newIds = [
      ...productDetails.data.buildings.map(x => x.id),
      selectedBuilding.id,
    ];
    await Api.patch(`instances/building-products/${newProduct.id}`, {
      buildings_ids: newIds,
    });
    await loadMetadata();
    setIsLoading(false);
  };
  const syncCharacteristics = async (newCharacteristic) => {
    setIsLoading(true);
    const characteristicDetails = await Api.get(`instances/building-characteristics/${newCharacteristic.id}`);
    const newIds = [
      ...characteristicDetails.data.buildings.map(x => x.id),
      selectedBuilding.id,
    ];
    await Api.patch(`instances/building-characteristics/${newCharacteristic.id}`, {
      buildings_ids: newIds,
    });
    await loadMetadata();
    setIsLoading(false);
  };
  return (
    <>
      <DeleteModal
        onDelete={deleteBuilding}
        isShown={isDeleteModalShown}
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalShown(false)}
        isAllowed={canDeleteBuilding()}
      >
        Are you sure you want to delete
        {' '}
        <strong>{selectedBuilding.name}</strong>
        {'? '}
        <br />
        This action cannot be undone.
      </DeleteModal>
      <div className="row">
        <div className="col-md-3">
          <div className="row">
            <div className="col-md-12">
              {selectedBuilding.images && selectedBuilding.images.length > 0 ? (
                <Carousel className="w-100">
                  {selectedBuilding.images.map(image => (
                    <Carousel.Item>
                      <img
                        className="d-block w-100 img-fluid"
                        src={image.url}
                        alt={image.label}
                        style={{
                          maxHeight: '400px',
                          objectFit: 'cover',
                        }}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              ) : (
                <img src="/images/default.jpg" alt={selectedBuilding.name} className="img-fluid" />
              )}
            </div>
            <div className="col-md-12 mt-4 d-grid gap-2">
              {user.role_id !== 3 && (
                <Button
                  variant={selectedBuilding.in_review === true ? 'success' : 'warning'}
                  onClick={async () => {
                    setIsPublishing(true);
                    const building = await publishBuilding(
                      selectedBuilding.id,
                      selectedBuilding.in_review,
                    );
                    dispatch(setSelectedBuildingAction(building));
                    dispatch(fetchBuildingsInitAction());
                    setIsPublishing(false);
                  }}
                >
                  {isPublishing ? (
                    <>
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="ms-2">Loading...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="check" />
                      <span className={`ms-2 ${selectedBuilding.in_review === true ? 'text-white' : ''}`}>
                        {selectedBuilding.in_review === true ? 'Approve' : 'Disapprove'}
                      </span>
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="dark"
                onClick={() => {
                  history.push('/buildings/edit');
                }}
              >
                <Icon icon="edit" />
                <span className="ms-2">Edit</span>
              </Button>
              <Button variant="dark" onClick={() => history.goBack()}>
                <Icon icon="times" />
                <span className="ms-2">Cancel</span>
              </Button>
              {user.role_id !== 3 && (
                <Button variant="danger" onClick={() => setIsDeleteModalShown(true)}>
                  {isDeleting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="ms-2">Loading...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="trash" />
                      <span className="ms-2">Delete</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row mb-3">
            <div className="col-md-12">
              <LiveInput endpoint="instances/buildings" fieldName="name" id={selectedBuilding.id} value={selectedBuilding.name} fontSize={22} />
            </div>
          </div>
          <div className="row border-top border-bottom mt-2 p-2">
            <div className="col-md-12">
              <div className="border-end d-inline pe-2 me-2">
                <span className="me-1">
                  <Icon icon="calendar" color="#000" />
                </span>
                {shortDateFormat(selectedBuilding.created_at)}
              </div>
              <div className="d-inline">
                {selectedBuilding.tags.map(tag => (
                  <span className="me-2 border-end pe-2">
                    <Icon icon="tag" color="#000" />
                    <span className="ms-2">
                      {tag.name}
                    </span>
                  </span>
                ))}
              </div>
              <div className="d-inline border-end d-inline pe-2 me-2">
                <Icon icon="map" color="#000" />
                <span className="ms-2">
                  {`${selectedBuilding.city.name}, ${selectedBuilding.city.country.name} [${selectedBuilding.city.code}]`}
                </span>
              </div>
              <div className="d-inline">
                <Icon icon="globe" color="#000" />
                <span className="ms-2">
                  {`${selectedBuilding.dynasty.name} [${selectedBuilding.dynasty.code}]`}
                </span>
              </div>
            </div>
          </div>
          <div className="row mt-5">
            <h6 className="ps-0">Building metadata</h6>
            <Table striped bordered hover>
              <tbody>
                <tr>
                  <td>Type</td>
                  <td>{`${selectedBuilding.building_type.name} [${selectedBuilding.building_type.code}]`}</td>
                </tr>
                <tr>
                  <td>Dynasty</td>
                  <td>{`${selectedBuilding.dynasty.name} [${selectedBuilding.dynasty.code}]`}</td>
                </tr>
                <tr>
                  <td>Number</td>
                  <td>{`${selectedBuilding.number}`}</td>
                </tr>
                <tr>
                  <td>Start Date</td>
                  <td>{selectedBuilding.start_date || '-'}</td>
                </tr>
                <tr>
                  <td>Finish Date</td>
                  <td>{selectedBuilding.end_date || '-'}</td>
                </tr>
                <tr>
                  <td>Founder</td>
                  <td>{selectedBuilding.founder || '-'}</td>
                </tr>
                <tr>
                  <td>Architect</td>
                  <td>{selectedBuilding.architect || '-'}</td>
                </tr>
                <tr>
                  <td>Region</td>
                  <td>
                    {`${selectedBuilding.city.name}, ${selectedBuilding.city.country.name} [${selectedBuilding.city.code}]`}
                  </td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td>
                    <Badge bg={selectedBuilding.in_review === true ? 'warning' : 'success'} pill>
                      {selectedBuilding.in_review ? 'In Review' : 'Published'}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td>Date Created</td>
                  <td>
                    {shortDateFormat(selectedBuilding.created_at)}
                  </td>
                </tr>
                <tr>
                  <td>Last Update</td>
                  <td>
                    {shortDateFormat(selectedBuilding.updated_at)}
                  </td>
                </tr>
                <tr>
                  <td>Location</td>
                  <td>
                    {selectedBuilding.location ? (
                      <a href={selectedBuilding.location} target="_blank" rel="noreferrer">
                        {selectedBuilding.location}
                      </a>
                    ) : 'Unknown'}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div
            className="row mt-5"
            dangerouslySetInnerHTML={{ __html: selectedBuilding.description }}
          />

          <ComponentsList
            components={components}
            isLoading={isLoading}
            onItemAdded={comp => syncComponents(comp)}
            cityId={selectedBuilding.city.id}
            dynastyId={selectedBuilding.dynasty.id}
          />

          <ElementsList
            elements={elements}
            isLoading={isLoading}
            onItemAdded={element => syncElements(element)}
            cityId={selectedBuilding.city.id}
            dynastyId={selectedBuilding.dynasty.id}
          />

          <ContextsList
            contexts={contexts}
            isLoading={isLoading}
            onItemAdded={con => syncContexts(con)}
            cityId={selectedBuilding.city.id}
            dynastyId={selectedBuilding.dynasty.id}
            buildingId={selectedBuilding.id}
            loadMetadata={loadMetadata}
          />

          <DocumentsList documents={selectedBuilding.documents} title="Documents" />
          <CitationList citations={citation} title="Building Citations" />
        </div>
        <div className="col-md-3">
          <Sidebar>
            <ProductsCardsList
              products={products}
              isLoading={isLoading}
              onItemAdded={product => syncProducts(product)}
              cityId={selectedBuilding.city.id}
              dynastyId={selectedBuilding.dynasty.id}
            />
            <CharacteristicsCardList
              characteristics={characteristics}
              isLoading={isLoading}
              onItemAdded={char => syncCharacteristics(char)}
              cityId={selectedBuilding.city.id}
              dynastyId={selectedBuilding.dynasty.id}
            />
          </Sidebar>
        </div>
      </div>
    </>
  );
};

export default ViewBuilding;
